import { useState, useCallback, useEffect } from 'react';
import {
  getDestinyRecords,
  deleteDestinyRecord,
  clearAllDestinyRecords,
  formatDestinyDate,
} from '@/utils/destinyStorage';
import type { DestinyRecord } from '@/utils/destinyStorage';
import MarkdownContent from '@/components/MarkdownContent';

export default function DestinyHistory() {
  const [records, setRecords] = useState<DestinyRecord[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const loadRecords = useCallback(() => {
    setRecords(getDestinyRecords());
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleDelete = useCallback(
    (id: string) => {
      deleteDestinyRecord(id);
      loadRecords();
    },
    [loadRecords]
  );

  const handleClearAll = useCallback(() => {
    clearAllDestinyRecords();
    setRecords([]);
    setShowClearConfirm(false);
  }, []);

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 animate-fade-in">
        <div className="w-16 h-16 flex items-center justify-center mb-4 bg-background-100 rounded-full border border-background-200">
          <i className="ri-book-2-line text-3xl text-foreground-300"></i>
        </div>
        <p className="text-sm text-foreground-500 mb-1">暂无命格记录</p>
        <p className="text-xs text-foreground-400">完成一次命格推算后，记录会自动保存在这里</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-foreground-400">共 {records.length} 条记录</p>
        <button
          onClick={() => setShowClearConfirm(true)}
          className="text-xs text-foreground-400 hover:text-accent-600 cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-delete-bin-line text-sm"></i>
          </div>
          清空记录
        </button>
      </div>

      {/* 清空确认弹窗 */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-6">
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-xs">
            <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 text-center">
              确认清空？
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-5">
              所有命格记录将被永久删除，无法恢复。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-background-200 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 py-2.5 rounded-xl bg-accent-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {records.map((record) => {
          const isExpanded = expandedId === record.id;
          const readingPreview =
            record.aiReading.slice(0, 100).replace(/[#*\n]/g, ' ') + '...';

          return (
            <div
              key={record.id}
              className="bg-background-100 rounded-xl border border-background-200 overflow-hidden"
            >
              {/* 记录头部 */}
              <div
                className="flex items-start gap-3 p-4 cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : record.id)}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-secondary-100 flex items-center justify-center border border-secondary-200">
                  <span className="font-heading text-xl font-bold text-secondary-600">
                    {record.dayStem}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading text-sm font-bold text-foreground-900">
                      {record.pillars.day[0]}
                      {record.pillars.day[1]}日主 · {record.dayStemWuxing}命
                    </span>
                    <span className="text-xs text-foreground-400">生肖{record.zodiac}</span>
                  </div>
                  <p className="text-xs text-foreground-600 mb-0.5">
                    公历 {record.solarDate} · 农历 {record.lunarDate}
                  </p>
                  {!isExpanded && (
                    <p className="text-xs text-foreground-400 truncate mt-0.5">
                      {readingPreview}
                    </p>
                  )}
                  <p className="text-xs text-foreground-400 mt-1">
                    {formatDestinyDate(record.createdAt)}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    {isExpanded ? (
                      <i className="ri-arrow-up-s-line text-base"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-base"></i>
                    )}
                  </div>
                </div>
              </div>

              {/* 展开详情 */}
              {isExpanded && (
                <div className="border-t border-background-200 px-4 pb-4 pt-3 space-y-3 animate-fade-in">
                  {/* 四柱信息 */}
                  <div>
                    <p className="text-xs font-medium text-foreground-600 mb-2">八字四柱：</p>
                    <div className="grid grid-cols-4 gap-2">
                      {(['年', '月', '日', '时'] as const).map((pillar, idx) => {
                        const keys: Array<'year' | 'month' | 'day' | 'hour'> = [
                          'year',
                          'month',
                          'day',
                          'hour',
                        ];
                        return (
                          <div
                            key={pillar}
                            className="text-center bg-background-50 rounded-lg py-2 border border-background-200"
                          >
                            <p className="text-xs text-foreground-400">{pillar}柱</p>
                            <p className="font-heading text-base font-bold text-foreground-900">
                              {record.pillars[keys[idx]][0]}
                            </p>
                            <p className="font-heading text-base font-bold text-foreground-700">
                              {record.pillars[keys[idx]][1]}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* AI 解读 */}
                  <div>
                    <p className="text-xs font-medium text-foreground-600 mb-2">命格解读：</p>
                    <div className="text-sm text-foreground-700 leading-relaxed">
                      <MarkdownContent text={record.aiReading} />
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handleDelete(record.id)}
                      className="flex-1 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-background-300 transition-all"
                    >
                      删除记录
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}